import React from 'react';
import { Card } from '../components/ui/card';
import { Shield, Award, Globe, Users, CheckCircle, Clock } from 'lucide-react';

export default function AboutUs() {
  return (
    <div className="min-h-screen py-16" data-testid="about-us-page">
      {/* Hero Section */}
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1280px]">
        <div className="text-center mb-16">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-foreground tracking-tight font-bold mb-6">
            About V watch
          </h1>
          <p className="text-xl text-primary font-medium mb-4">
            Verified Timepieces. Curated Trust.
          </p>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            The world's premier marketplace for authenticated luxury timepieces. Where collectors, enthusiasts, and dealers come together with complete confidence.
          </p>
        </div>

        {/* Mission Statement */}
        <Card className="p-8 md:p-12 mb-16 border-border bg-card/80 backdrop-blur" data-testid="mission-section">
          <h2 className="font-display text-3xl text-foreground font-semibold mb-6 text-center">
            Our Mission
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed text-center max-w-4xl mx-auto">
            To revolutionize the luxury watch marketplace by establishing an unprecedented standard of trust, transparency, and security. We believe every transaction should be protected, every timepiece authenticated, and every collector empowered to trade with absolute confidence.
          </p>
        </Card>

        {/* Core Values */}
        <div className="mb-16">
          <h2 className="font-display text-3xl text-foreground font-semibold mb-8 text-center">
            What Sets Us Apart
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 border-border bg-card group hover:shadow-2xl transition-all" data-testid="value-authentication">
              <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                <Shield className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-xl text-foreground mb-3 font-semibold">
                Authentication First
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Every watch undergoes rigorous authentication by certified horologists before listing. Our multi-point inspection process ensures only genuine timepieces reach our marketplace.
              </p>
            </Card>

            <Card className="p-8 border-border bg-card group hover:shadow-2xl transition-all" data-testid="value-trust">
              <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                <Award className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-xl text-foreground mb-3 font-semibold">
                Verified Sellers
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                We carefully vet every seller on our platform. From established dealers to private collectors, each undergoes identity verification and background checks before joining our community.
              </p>
            </Card>

            <Card className="p-8 border-border bg-card group hover:shadow-2xl transition-all" data-testid="value-security">
              <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                <Globe className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-xl text-foreground mb-3 font-semibold">
                Global Marketplace
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Connect with buyers and sellers across 100+ countries. Fully insured international shipping, customs support, and white-glove service for every transaction, anywhere in the world.
              </p>
            </Card>
          </div>
        </div>

        {/* How It Works */}
        <div className="mb-16">
          <h2 className="font-display text-3xl text-foreground font-semibold mb-8 text-center">
            How V watch Works
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-6 border-border bg-card" data-testid="step-1">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 text-primary font-bold mb-4">
                1
              </div>
              <h3 className="font-display text-lg text-foreground mb-2 font-semibold">
                List Your Watch
              </h3>
              <p className="text-sm text-muted-foreground">
                Sellers submit detailed information and photos of their timepiece for authentication review.
              </p>
            </Card>

            <Card className="p-6 border-border bg-card" data-testid="step-2">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 text-primary font-bold mb-4">
                2
              </div>
              <h3 className="font-display text-lg text-foreground mb-2 font-semibold">
                Authentication
              </h3>
              <p className="text-sm text-muted-foreground">
                Our experts verify authenticity, condition, and documentation. Only approved watches go live.
              </p>
            </Card>

            <Card className="p-6 border-border bg-card" data-testid="step-3">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 text-primary font-bold mb-4">
                3
              </div>
              <h3 className="font-display text-lg text-foreground mb-2 font-semibold">
                Secure Transaction
              </h3>
              <p className="text-sm text-muted-foreground">
                Payments held in escrow until delivery. Both buyer and seller fully protected throughout.
              </p>
            </Card>

            <Card className="p-6 border-border bg-card" data-testid="step-4">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 text-primary font-bold mb-4">
                4
              </div>
              <h3 className="font-display text-lg text-foreground mb-2 font-semibold">
                Insured Delivery
              </h3>
              <p className="text-sm text-muted-foreground">
                Professional packaging and fully insured shipping. Track your timepiece every step of the way.
              </p>
            </Card>
          </div>
        </div>

        {/* Trust & Safety */}
        <Card className="p-8 md:p-12 mb-16 border-border bg-card/80" data-testid="trust-safety-section">
          <h2 className="font-display text-3xl text-foreground font-semibold mb-8 text-center">
            Trust & Safety Commitment
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-medium text-foreground mb-1">Multi-Point Authentication</h4>
                  <p className="text-sm text-muted-foreground">
                    Serial number verification, movement inspection, material analysis, and documentation review.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-medium text-foreground mb-1">Escrow Payment Protection</h4>
                  <p className="text-sm text-muted-foreground">
                    Funds held securely until successful delivery and buyer confirmation. Full refund guarantee.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-medium text-foreground mb-1">Seller Verification</h4>
                  <p className="text-sm text-muted-foreground">
                    Identity checks, business validation, and reputation tracking for every seller on our platform.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-medium text-foreground mb-1">Insurance Coverage</h4>
                  <p className="text-sm text-muted-foreground">
                    Every shipment fully insured up to declared value. Zero risk during transit.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-medium text-foreground mb-1">Dispute Resolution</h4>
                  <p className="text-sm text-muted-foreground">
                    Dedicated support team to mediate any issues. Fair resolution guaranteed.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-medium text-foreground mb-1">24/7 Customer Support</h4>
                  <p className="text-sm text-muted-foreground">
                    Expert concierge team available around the clock for assistance and guidance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          <Card className="p-6 text-center border-border bg-card" data-testid="stat-transactions">
            <div className="font-display text-4xl text-primary font-bold mb-2">10K+</div>
            <div className="text-sm text-muted-foreground">Verified Transactions</div>
          </Card>
          <Card className="p-6 text-center border-border bg-card" data-testid="stat-value">
            <div className="font-display text-4xl text-primary font-bold mb-2">$2B+</div>
            <div className="text-sm text-muted-foreground">Total Transaction Value</div>
          </Card>
          <Card className="p-6 text-center border-border bg-card" data-testid="stat-sellers">
            <div className="font-display text-4xl text-primary font-bold mb-2">500+</div>
            <div className="text-sm text-muted-foreground">Verified Sellers</div>
          </Card>
          <Card className="p-6 text-center border-border bg-card" data-testid="stat-countries">
            <div className="font-display text-4xl text-primary font-bold mb-2">100+</div>
            <div className="text-sm text-muted-foreground">Countries Served</div>
          </Card>
        </div>

        {/* Team Section */}
        <div className="mb-16">
          <h2 className="font-display text-3xl text-foreground font-semibold mb-6 text-center">
            Our Expertise
          </h2>
          <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto mb-8">
            Founded by watch collectors and industry veterans with decades of combined experience in luxury horology, authentication, and secure commerce.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6 border-border bg-card text-center">
              <Users className="h-10 w-10 text-primary mx-auto mb-4" />
              <h4 className="font-medium text-foreground mb-2">Certified Horologists</h4>
              <p className="text-sm text-muted-foreground">
                Expert watchmakers with decades of authentication experience
              </p>
            </Card>
            <Card className="p-6 border-border bg-card text-center">
              <Clock className="h-10 w-10 text-primary mx-auto mb-4" />
              <h4 className="font-medium text-foreground mb-2">Industry Veterans</h4>
              <p className="text-sm text-muted-foreground">
                Leaders from major auction houses and luxury watch retailers
              </p>
            </Card>
            <Card className="p-6 border-border bg-card text-center">
              <Shield className="h-10 w-10 text-primary mx-auto mb-4" />
              <h4 className="font-medium text-foreground mb-2">Security Experts</h4>
              <p className="text-sm text-muted-foreground">
                Specialists in secure payments, fraud prevention, and risk management
              </p>
            </Card>
          </div>
        </div>

        {/* CTA Section */}
        <Card className="p-12 text-center border-border bg-gradient-to-br from-card to-muted" data-testid="cta-section">
          <h2 className="font-display text-3xl text-foreground font-semibold mb-4">
            Join the V watch Community
          </h2>
          <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
            Whether you're buying your first luxury timepiece or expanding a world-class collection, V watch is your trusted partner in the journey.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => window.location.href = '/catalog'}
              className="px-8 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
              data-testid="cta-browse-button"
            >
              Browse Collection
            </button>
            <button
              onClick={() => window.location.href = '/register'}
              className="px-8 py-3 border border-border bg-card text-foreground rounded-lg font-medium hover:bg-muted transition-colors"
              data-testid="cta-join-button"
            >
              Start Selling
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}
